/**
 * Editing one survey email.
 *
 * Two things worth knowing before changing anything here:
 *
 * 1. The fields are declared by the SERVER, on the email definition, and this renders
 *    whatever it is handed. That is not indirection for its own sake — it is what
 *    keeps the editor from offering a field the send path does not read. An email
 *    editor that saves cleanly and changes nothing about the mail is worse than no
 *    editor, because it is believed.
 *
 * 2. The preview is rendered by the SAME functions that send the mail, server-side.
 *    There is no local copy of the brand shell here to drift out of step with the
 *    real one, and there must never be.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Skeleton,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import apiService from '../../services/api';
import { SurveyEmail, SurveyEmailField, SurveyEmailsResponse } from './types';

const TEAL = '#1A5F7A';

interface Props {
  email: SurveyEmail;
  /** The optimistic-lock token as of the list read. Sent back on save. */
  rev: number;
  /** Which survey this email belongs to. */
  campaign?: string | null;
  onClose: () => void;
  onSaved: (res: SurveyEmailsResponse) => void;
}

type Values = Record<string, string | string[]>;

const sameValues = (a: Values, b: Values) => JSON.stringify(a) === JSON.stringify(b);

const SurveyEmailEditModal: React.FC<Props> = ({ email, rev, campaign = null, onClose, onSaved }) => {
  const baseline = useMemo(() => email.values as Values, [email]);
  const [values, setValues] = useState<Values>(() => structuredClone(baseline));

  const [preview, setPreview] = useState<{ subject: string; html: string } | null>(null);
  const [previewing, setPreviewing] = useState(true);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [width, setWidth] = useState<'desktop' | 'mobile'>('desktop');

  /**
   * Paragraph fields are stored as a string[] but edited as ONE block of prose,
   * because that is how anyone actually writes an email — a stack of two-row boxes
   * makes you compose a paragraph at a time and hides the shape of the whole thing.
   *
   * The raw text is held separately from the split array on purpose. Deriving the
   * textarea's value from `values[name].join('\n\n')` would delete the blank line the
   * moment you typed it: the split drops the trailing empty, the join renders one
   * fewer newline, and the cursor jumps. So the textarea owns the text, and the array
   * is recomputed from it on every keystroke.
   */
  const [rawText, setRawText] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      email.fields
        .filter((f) => f.type === 'paragraphs')
        .map((f) => [f.name, ((baseline[f.name] as string[]) || []).join('\n\n')])
    )
  );

  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [note, setNote] = useState<{ severity: 'success' | 'error' | 'warning'; text: string } | null>(
    null
  );

  const dirty = !sameValues(values, baseline);

  /**
   * Previews what is on screen, not what is stored — otherwise an admin would have to
   * save to see their own wording, which defeats the point of a preview.
   *
   * Debounced, and the in-flight response is discarded if the values moved on while
   * it was travelling. Without that guard a slow render of an early keystroke lands
   * after a fast render of a later one and the preview silently shows stale copy.
   */
  const seq = useRef(0);
  const runPreview = useCallback(
    async (v: Values) => {
      const mine = ++seq.current;
      setPreviewing(true);
      setPreviewError(null);
      try {
        const res = await apiService.renderSurveyEmail({ id: email.id, values: v }, campaign || undefined);
        if (mine !== seq.current) return;
        setPreview({ subject: res.subject, html: res.html });
      } catch (e: any) {
        if (mine !== seq.current) return;
        setPreviewError(e?.message || 'Could not render a preview.');
        setPreview(null);
      }
      if (mine === seq.current) setPreviewing(false);
    },
    [email.id, campaign]
  );

  useEffect(() => {
    const t = setTimeout(() => runPreview(values), 400);
    return () => clearTimeout(t);
  }, [values, runPreview]);

  const set = (name: string, v: string | string[]) =>
    setValues((prev) => ({ ...prev, [name]: v }));

  /** One blank line between paragraphs — the same break the renderer emits as a <p>. */
  const splitParagraphs = (text: string): string[] =>
    text
      .split(/\n\s*\n+/)
      .map((p) => p.replace(/\s+$/, ''))
      .filter((p) => p.trim() !== '');

  const setParagraphs = (name: string, text: string) => {
    setRawText((prev) => ({ ...prev, [name]: text }));
    set(name, splitParagraphs(text));
  };

  const save = async () => {
    setSaving(true);
    setNote(null);
    try {
      const res = await apiService.updateSurveyEmail(
        { id: email.id, values, expected_rev: rev },
        campaign || undefined
      );
      onSaved(res);
    } catch (e: any) {
      const msg = String(e?.message || '');
      setNote({
        severity: 'error',
        text: /409|conflict/i.test(msg)
          ? 'Someone else changed these emails while you were editing. Close and reopen to see their version — saving now would overwrite it.'
          : /403|PERMISSION/i.test(msg)
            ? 'You have view-only access to this area, so this cannot be changed.'
            : msg || 'Could not save.',
      });
      setSaving(false);
    }
  };

  const testSend = async () => {
    setTesting(true);
    setNote(null);
    try {
      // No recipient: the Lambda uses the acting admin's own address from the session
      // header, so a test can never be aimed at a member by accident.
      const res = await apiService.testSendSurveyEmail({ id: email.id, values }, campaign || undefined);
      setNote({ severity: 'success', text: `Sent to ${res.to}. Check your inbox.` });
    } catch (e: any) {
      setNote({ severity: 'error', text: e?.message || 'The test send failed.' });
    }
    setTesting(false);
  };

  const attemptClose = () => {
    if (dirty && !window.confirm('Discard your unsaved changes to this email?')) return;
    onClose();
  };

  const renderField = (field: SurveyEmailField) => {
    if (field.type === 'paragraphs') {
      const text = rawText[field.name] ?? '';
      const count = splitParagraphs(text).length;
      return (
        <TextField
          key={field.name}
          fullWidth
          multiline
          minRows={12}
          maxRows={28}
          label={field.label}
          required={field.required}
          value={text}
          onChange={(e) => setParagraphs(field.name, e.target.value)}
          sx={{ mb: 2, '& .MuiInputBase-input': { fontSize: 14, lineHeight: 1.6 } }}
          helperText={`Leave a blank line between paragraphs. ${count} paragraph${count === 1 ? '' : 's'}.`}
        />
      );
    }

    return (
      <TextField
        key={field.name}
        fullWidth
        size="small"
        label={field.label}
        required={field.required}
        multiline={field.type === 'textarea'}
        minRows={field.type === 'textarea' ? 2 : undefined}
        value={(values[field.name] as string) ?? ''}
        onChange={(e) => set(field.name, e.target.value)}
        sx={{ mb: 2 }}
        helperText={
          field.name === 'preheader'
            ? 'The line the inbox shows under the subject. Never put a price or a discount here — it is the strongest single “this is an advert” signal.'
            : undefined
        }
      />
    );
  };

  return (
    <Dialog open onClose={attemptClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: 16, fontWeight: 700 }}>
        {email.label}
        {!email.body_editable && (
          <Chip
            size="small"
            label="Fixed design"
            sx={{ ml: 1.5, height: 20, fontSize: 10, fontWeight: 700, bgcolor: '#EEF2FF', color: '#3730A3' }}
          />
        )}
      </DialogTitle>

      <DialogContent dividers>
        {note && (
          <Alert severity={note.severity} onClose={() => setNote(null)} sx={{ mb: 2, fontSize: 13 }}>
            {note.text}
          </Alert>
        )}

        {!email.body_editable && (
          <Alert severity="info" icon={false} sx={{ mb: 2, fontSize: 13, bgcolor: '#F8FAFC' }}>
            This email's body is a fixed design that was reviewed and signed off line by line,
            so it is not editable here. The subject and the inbox preview line are.
          </Alert>
        )}

        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <Box sx={{ flex: '1 1 380px', minWidth: 320 }}>
            {email.fields.map(renderField)}

            <Typography sx={{ fontSize: 11, color: '#9CA3AF' }}>
              Sent from {email.sender_name || '—'} &lt;{email.sender_email || '—'}&gt;
            </Typography>
          </Box>

          <Box sx={{ flex: '1 1 420px', minWidth: 320 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#374151', flexGrow: 1 }}>
                Preview
              </Typography>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={width}
                onChange={(_e, v) => v && setWidth(v)}
              >
                <ToggleButton value="desktop" sx={{ textTransform: 'none', fontSize: 12 }}>
                  Desktop
                </ToggleButton>
                <ToggleButton value="mobile" sx={{ textTransform: 'none', fontSize: 12 }}>
                  Mobile
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {previewError && (
              <Alert severity="warning" sx={{ mb: 1, fontSize: 13 }}>
                {previewError}
              </Alert>
            )}

            {preview && (
              <Typography sx={{ fontSize: 12, color: '#6B7280', mb: 1 }}>
                Subject: <strong>{preview.subject}</strong>
              </Typography>
            )}

            {previewing && !preview ? (
              <Skeleton variant="rectangular" height={420} sx={{ borderRadius: 2 }} />
            ) : (
              <Box
                sx={{
                  border: '1px solid #E5E7EB',
                  borderRadius: '10px',
                  overflow: 'hidden',
                  bgcolor: '#F9FAFB',
                  display: 'flex',
                  justifyContent: 'center',
                  opacity: previewing ? 0.6 : 1,
                  transition: 'opacity .15s',
                }}
              >
                <iframe
                  // sandbox with no allow-scripts: this renders admin-entered copy and
                  // there is no reason for it to be able to run anything.
                  sandbox=""
                  title={`${email.label} preview`}
                  srcDoc={preview?.html || ''}
                  style={{
                    width: width === 'mobile' ? 375 : '100%',
                    height: 480,
                    border: 'none',
                    background: '#fff',
                  }}
                />
              </Box>
            )}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button
          onClick={testSend}
          disabled={testing || saving}
          startIcon={testing ? <CircularProgress size={14} /> : <SendIcon sx={{ fontSize: 16 }} />}
          sx={{ textTransform: 'none', color: TEAL, mr: 'auto' }}
        >
          {testing ? 'Sending…' : 'Send a test to me'}
        </Button>
        <Button onClick={attemptClose} sx={{ textTransform: 'none', color: '#6B7280' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={save}
          disabled={!dirty || saving}
          sx={{ textTransform: 'none', bgcolor: TEAL, '&:hover': { bgcolor: '#14506B' } }}
        >
          {saving ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SurveyEmailEditModal;
